import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from '../../services/api';

/**
 * 用户资料管理 Hook
 * 统一管理用户资料的获取、更新和状态管理
 */
export const useUserProfile = () => {
  const { user, updateUser, loading: authLoading, isInitialized } = useAuth();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const abortControllerRef = useRef(null);

  // 获取用户资料
  const fetchUserProfile = useCallback(async (showRefreshIndicator = false) => {
    try {
      // 取消之前的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // 创建新的 AbortController
      abortControllerRef.current = new AbortController();

      if (showRefreshIndicator) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError('');

      const response = await axios.get('/users/me', {
        signal: abortControllerRef.current.signal
      });

      const userData = response.data;
      setCurrentUser(userData);

      // 同步更新全局用户状态
      if (updateUser && userData) {
        updateUser(userData);
      }

    } catch (error) {
      // 忽略取消的请求（兼容 Axios 的取消错误）
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        return;
      }

      console.error('获取用户信息失败:', error);
      
      // 分类错误处理
      let errorMessage = '获取用户信息失败，请重试';
      
      if (error.response) {
        const status = error.response.status;
        switch (status) {
          case 401:
            errorMessage = '登录已过期，请重新登录';
            break;
          case 403:
            errorMessage = '没有权限访问用户信息';
            break;
          case 404:
            errorMessage = '用户信息不存在';
            break;
          case 500:
            errorMessage = '服务器错误，请稍后重试';
            break;
          default:
            errorMessage = error.response.data?.message || errorMessage;
        }
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = '网络连接失败，请检查网络设置';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
      abortControllerRef.current = null;
    }
  }, [updateUser]);

  // 更新头像
  const updateAvatar = useCallback((newAvatarUrl) => {
    const updatedUser = {
      ...currentUser,
      avatar: newAvatarUrl
    };

    setCurrentUser(updatedUser);

    // 同步更新全局状态
    if (updateUser) {
      updateUser(updatedUser);
    }
  }, [currentUser, updateUser]);

  // 更新用户资料
  const updateProfile = useCallback((updatedUserData) => {
    setCurrentUser(updatedUserData);

    // 同步更新全局状态
    if (updateUser) {
      updateUser(updatedUserData);
    }
  }, [updateUser]);

  // 重试获取用户信息
  const retryFetch = useCallback(() => {
    fetchUserProfile(true);
  }, [fetchUserProfile]);

  // 清除错误状态
  const clearError = useCallback(() => {
    setError('');
  }, []);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // 总是请求最新的用户信息以确保登录状态有效性
  useEffect(() => {
    if (authLoading) {
      // AuthContext还在加载中，等待
      setIsLoading(true);
      return;
    }

    // 如果全局有用户且已经完成初始化，优先使用全局数据，避免重复请求
    if (isInitialized && user) {
      setCurrentUser(user);
      setIsLoading(false);
      return;
    }

    // 无论是否有用户数据，都请求最新信息以验证登录状态
    fetchUserProfile();
  }, [authLoading, isInitialized, user, fetchUserProfile]);

  return {
    currentUser,
    isLoading,
    isRefreshing,
    error,
    updateAvatar,
    updateProfile,
    retryFetch,
    clearError,
    refetch: fetchUserProfile
  };
};
